// 工艺类型 (适配工艺类型) API.
// cap-types 驱动: 材料库/材料详情/SKU库 的工艺类型徽标 + 工艺类型管理页.
// 2026-09-08: "具备设备"改 deviceKeys 显式绑定 (不再按光源反查); 光源/动作退化为可选描述 (非激光留空).

import { request, requestWithMsg, MsgResult } from './request';

export interface CapType {
    /** 业务键, 如 blueEng / bladeCut (只是标识, 含义由 label/光源/动作承载, 可改名) */
    capKey: string;
    /** 展示名, 如 蓝光雕 */
    label: string;
    /** 光源: light_source 字典 code (blue/red/ir/co2/uv...); 非激光工艺类型为空串 */
    lightSource: string;
    /** 动作: cap_action 字典 code (eng 雕刻 / cut 切割...); 可空 */
    action: string;
    sort: number;
    /** 具备该工艺类型的设备 (显式绑定, 含停用设备; 反查接口只回启用中的). 空 = 未绑定 */
    deviceKeys: string[];
}

export interface CapTypeDevice {
    deviceKey: string;
    /** 设备名称 (展示用, 空则回退 deviceKey) */
    name?: string;
    family: string;
    archCode: string;
    source: string;
    stdW: number;
}

const BASE = '/v1';

export function listCapTypes(): Promise<CapType[]> {
    return request<CapType[]>(`${BASE}/cap-types`);
}

// 写接口: 统一走审批流 (message 含"已提交审批", data={taskCode}).
export function createCapType(payload: {
    capKey: string;
    label: string;
    lightSource?: string;
    action?: string;
    deviceKeys?: string[];
}): Promise<MsgResult<{ capKey?: string; taskCode?: string }>> {
    return requestWithMsg(`${BASE}/cap-types`, { method: 'POST', body: payload });
}

/** 编辑工艺类型; 统一走审批. deviceKeys 传 [] = 清空绑定, 不传 = 不动.
 *  newCapKey 非空且与 capKey 不同 = 改名 (后端级联更新材料能力位的引用). */
export function updateCapType(
    capKey: string,
    payload: {
        newCapKey?: string;
        label?: string;
        lightSource?: string;
        action?: string;
        sort?: number;
        deviceKeys?: string[];
    },
): Promise<MsgResult<null>> {
    // 业务键走 body (后端 updateReq.capKey 必填)
    return requestWithMsg(`${BASE}/cap-types/update`, {
        method: 'POST',
        body: { capKey, ...payload },
    });
}

export function deleteCapType(capKey: string): Promise<MsgResult<{ taskCode?: string } | null>> {
    return requestWithMsg(`${BASE}/cap-types/delete`, {
        method: 'POST',
        body: { capKey },
    });
}

/** 具备某工艺类型的设备列表 (= deviceKeys 绑定里启用中的, 带名称/框架/光源/功率展示字段). */
export function listCapTypeDevices(capKey: string): Promise<CapTypeDevice[]> {
    return request<CapTypeDevice[]>(`${BASE}/cap-types/devices`, {
        method: 'POST', dedupe: true,
        body: { capKey },
    });
}

// 光源/动作的可选枚举与展示名均已字典化 (字典维护 category=light_source / cap_action):
// 下拉用 DictSelect, 展示映射用 useDictText, 此处不再维护硬编码选项表.
// **列表/详情一律展示 label, 不渲染 code** (blue→蓝光, eng→雕刻).
